// src/constants/payoutStructures.js

/**
 * Round/range-based payout templates.
 * For brackets, each band is a ROUND (stage). `percent` is the TOTAL for that stage.
 * Your prize code should split the band's percent evenly across everyone in that stage.
 *
 * Firestore doc IDs to create (examples below include):
 *   - h2h-winner-take-all
 *   - bracket-4-team, bracket-8-team, bracket-16-team, ..., bracket-16k-team
 *   - wgn-6, wgn-5, wgn-4   (streak-based)
 *   - season-ladder-100     (Top 100 placements)
 *   - Racing payouts for GT7, FZH, and MKD
 *   - 16k, 8k, 4k, 2k, 1k, ..., 4
 *   - racing-GT7
 *   - racing-fzh
 *   - racing-mkd
 */



export const PAYOUT_STRUCTURES = {
  // ------------------------
  // Head-to-Head
  // ------------------------
  'h2h-winner-take-all': {
    name: 'H2H - Winner Take All',
    plainName: 'H2H - Single Match',
    description: '100% of the Crit pool goes to the winner.',
    plainDescription: 'One head-to-head match decides the winner.',
    totalRounds: 1,
    bands: [
      { stage: 'Champion', minRank: 1, maxRank: 1, percent: 100 },
    ],
  },

  // ------------------------
  // Brackets (Teams) — bands are rounds
  // ------------------------

  // 4 teams — pays Top 2
  'bracket-4-team': {
    name: 'Bracket - 4 Slots Winner Takes All',
    plainName: 'Bracket - 4 Slots',
    description: '4 groups enter, one takes all!',
    plainDescription: 'Single-elimination bracket: 4 slots, 2 rounds.',
    totalRounds: 2,
    bands: [
      { stage: 'Champion',  minRank: 1, maxRank: 1, percent: 100 },
    ],
  },

  // 8 teams — pays Top 2
  'bracket-8-team': {
    name: 'Bracket - 8 Slots (Pays Top 2)',
    plainName: 'Bracket - 8 Slots',
    description: 'Payouts to Finals only.',
    plainDescription: 'Single-elimination bracket: 8 slots, 3 rounds.',
    totalRounds: 3,
    bands: [
      { stage: 'Champion',  minRank: 1, maxRank: 1, percent: 65 },
      { stage: 'Runner-up', minRank: 2, maxRank: 2, percent: 35 },
    ],
  },

  // 16 teams — pays Top 4 (Finals + Semifinals)
  'bracket-16-team': {
    name: 'Bracket - 16 Slots (Pays Top 4)',
    plainName: 'Bracket - 16 Slots',
    description: 'Payouts to Finals and Semifinals.',
    plainDescription: 'Single-elimination bracket: 16 slots, 4 rounds.',
    totalRounds: 4,
    bands: [
      { stage: 'Champion',    minRank: 1, maxRank: 1, percent: 50 },
      { stage: 'Runner-up',   minRank: 2, maxRank: 2, percent: 30 },
      { stage: 'Semifinals',  minRank: 3, maxRank: 4, percent: 20 }, // 10% each
    ],
  },

  // 32 teams — pays Top 8 (Finals + Semis + Quarters)
  'bracket-32-team': {
    name: 'Bracket - 32 Slots (Pays Top 8)',
    plainName: 'Bracket - 32 Slots',
    description: 'Round-based payouts through Quarterfinals.',
    plainDescription: 'Single-elimination bracket: 32 slots, 5 rounds.',
    totalRounds: 5,
    bands: [
      { stage: 'Champion',       minRank: 1, maxRank: 1, percent: 40 },
      { stage: 'Runner-up',      minRank: 2, maxRank: 2, percent: 25 },
      { stage: 'Semifinals',     minRank: 3, maxRank: 4, percent: 15 }, // 7.5 each
      { stage: 'Quarterfinals',  minRank: 5, maxRank: 8, percent: 20 }, // 5 each
    ],
  },

  // 64 teams — pays Top 16
  'bracket-64-team': {
    name: 'Bracket - 64 Slots (Pays Top 16)',
    plainName: 'Bracket - 64 Slots',
    description: 'Round-based payouts through top 16 slots.',
    plainDescription: 'Single-elimination bracket: 64 slots, 6 rounds.',
    totalRounds: 6,
    bands: [
      { stage: 'Champion',       minRank: 1,  maxRank: 1,  percent: 28 },
      { stage: 'Runner-up',      minRank: 2,  maxRank: 2,  percent: 16 },
      { stage: 'Semifinals',     minRank: 3,  maxRank: 4,  percent: 16 }, // 8 each
      { stage: 'Quarterfinals',  minRank: 5,  maxRank: 8,  percent: 16 }, // 4 each
      { stage: 'Round 2',    minRank: 9,  maxRank: 16, percent: 24 }, // 1.5 each
    ],
  },

  // 128 teams — pays Top 32
  'bracket-128-team': {
    name: 'Bracket - 128 Slots (Pays Top 32)',
    plainName: 'Bracket - 128 Slots',
    description: 'Round-based payouts through top 32 slots.',
    plainDescription: 'Single-elimination bracket: 128 slots, 7 rounds.',
    totalRounds: 7,
    bands: [
      { stage: 'Champion',       minRank: 1,  maxRank: 1,  percent: 26 },
      { stage: 'Runner-up',      minRank: 2,  maxRank: 2,  percent: 14 },
      { stage: 'Semifinals',     minRank: 3,  maxRank: 4,  percent: 12 },   // 6 each
      { stage: 'Quarterfinals',  minRank: 5,  maxRank: 8,  percent: 12 },   // 3 each
      { stage: 'Round 3',    minRank: 9,  maxRank: 16, percent: 16 },   // 2 each
      { stage: 'Round 2',    minRank: 17, maxRank: 32, percent: 20 },   // 1.25 each
    ],
  },

  // 256 teams — pays Top 64
  'bracket-256-team': {
    name: 'Bracket - 256 Slots (Pays Top 64)',
    plainName: 'Bracket - 256 Slots',
    description: 'Round-based payouts through top 64 slots.',
    plainDescription: 'Single-elimination bracket: 256 slots, 8 rounds.',
    totalRounds: 8,
    bands: [
      { stage: 'Champion',       minRank: 1,  maxRank: 1,  percent: 22 },
      { stage: 'Runner-up',      minRank: 2,  maxRank: 2,  percent: 12 },
      { stage: 'Semifinals',     minRank: 3,  maxRank: 4,  percent: 14 },   // 7 each
      { stage: 'Quarterfinals',  minRank: 5,  maxRank: 8,  percent: 12 },   // 3 each
      { stage: 'Round 4',    minRank: 9,  maxRank: 16, percent: 12 },   // 1.5 each
      { stage: 'Round 3',    minRank: 17, maxRank: 32, percent: 12 },   // 0.75 each
      { stage: 'Round 2',    minRank: 33, maxRank: 64, percent: 16 },   // 0.25 each
    ],
  },

  // 512 teams — pays Top 128
  'bracket-512-team': {
    name: 'Bracket - 512 Slots (Pays Top 128)',
    plainName: 'Bracket - 512 Slots',
    description: 'Round-based payouts through top 128 slots.',
    plainDescription: 'Single-elimination bracket: 512 slots, 9 rounds.',
    totalRounds: 9,
    bands: [
      { stage: 'Champion',       minRank: 1,   maxRank: 1,   percent: 20 },
      { stage: 'Runner-up',      minRank: 2,   maxRank: 2,   percent: 11 },
      { stage: 'Semifinals',     minRank: 3,   maxRank: 4,   percent: 12 }, // 6 each
      { stage: 'Quarterfinals',  minRank: 5,   maxRank: 8,   percent: 12 }, // 3 each
      { stage: 'Round 5',    minRank: 9,   maxRank: 16,  percent: 12 }, // 1.5 each
      { stage: 'Round 4',    minRank: 17,  maxRank: 32,  percent: 12 }, // 0.75 each
      { stage: 'Round 3',    minRank: 33,  maxRank: 64,  percent: 11 }, // ~0.34375 each
      { stage: 'Round 2',   minRank: 65,  maxRank: 128, percent: 10 }, // ~0.15625 each
    ],
  },

  // 1,024 teams — pays Top 256
  'bracket-1k-team': {
    name: 'Bracket - 1,024 Slots (Pays Top 256)',
    plainName: 'Bracket - 1,024 Slots',
    description: 'Round-based payouts through top 256 slots.',
    plainDescription: 'Single-elimination bracket: 1,024 slots, 10 rounds.',
    totalRounds: 10,
    bands: [
      { stage: 'Champion',       minRank: 1,    maxRank: 1,    percent: 18 },
      { stage: 'Runner-up',      minRank: 2,    maxRank: 2,    percent: 10 },
      { stage: 'Semifinals',     minRank: 3,    maxRank: 4,    percent: 12 },  // 6 each
      { stage: 'Quarterfinals',  minRank: 5,    maxRank: 8,    percent: 12 },  // 3 each
      { stage: 'Round 6',    minRank: 9,    maxRank: 16,   percent: 12 },  // 1.5 each
      { stage: 'Round 5',    minRank: 17,   maxRank: 32,   percent: 12 },  // 0.75 each
      { stage: 'Round 4',    minRank: 33,   maxRank: 64,   percent: 12 },  // 0.1875 each
      { stage: 'Round 3',   minRank: 65,   maxRank: 128,  percent: 7 },   // ~0.0546875 each
      { stage: 'Round 2',   minRank: 129,  maxRank: 256,  percent: 5 },   // ~0.01953125 each
    ],
  },

  // 2,048 teams — pays Top 512
  'bracket-2k-team': {
    name: 'Bracket - 2,048 Slots (Pays Top 512)',
    plainName: 'Bracket - 2,048 Slots',
    description: 'Round-based payouts through top 512 slots.',
    plainDescription: 'Single-elimination bracket: 2,048 slots, 11 rounds.',
    totalRounds: 11,
    bands: [
      { stage: 'Champion',       minRank: 1,    maxRank: 1,    percent: 18 },
      { stage: 'Runner-up',      minRank: 2,    maxRank: 2,    percent: 10 },
      { stage: 'Semifinals',     minRank: 3,    maxRank: 4,    percent: 12 },  // 5.5 each
      { stage: 'Quarterfinals',  minRank: 5,    maxRank: 8,    percent: 11 },  // 2.75 each
      { stage: 'Round 7',    minRank: 9,    maxRank: 16,   percent: 11 },  // 1.375 each
      { stage: 'Round 6',    minRank: 17,   maxRank: 32,   percent: 10 },  // 0.6875 each
      { stage: 'Round 5',    minRank: 33,   maxRank: 64,   percent: 10 },  // ~0.34375 each
      { stage: 'Round 4',   minRank: 65,   maxRank: 128,  percent: 8 },  // ~0.078125 each
      { stage: 'Round 3',   minRank: 129,  maxRank: 256,  percent: 6 },   // ~0.0234375 each
      { stage: 'Round 2',   minRank: 257,  maxRank: 512,  percent: 4 },   // ~0.005859375 each
    ],
  },

  // 4,096 teams — pays Top 1,024
  'bracket-4k-team': {
    name: 'Bracket - 4,096 Slots (Pays Top 1,024)',
    plainName: 'Bracket - 4,096 Slots',
    description: 'Round-based payouts through top 1,024 slots.',
    plainDescription: 'Single-elimination bracket: 4,096 slots, 12 rounds.',
    totalRounds: 12,
    bands: [
      { stage: 'Champion',       minRank: 1,     maxRank: 1,     percent: 16 },
      { stage: 'Runner-up',      minRank: 2,     maxRank: 2,     percent: 8 },
      { stage: 'Semifinals',     minRank: 3,     maxRank: 4,     percent: 10 }, // 5 each
      { stage: 'Quarterfinals',  minRank: 5,     maxRank: 8,     percent: 10 }, // 2.5 each
      { stage: 'Round 8',    minRank: 9,     maxRank: 16,    percent: 10 }, // 1.25 each
      { stage: 'Round 7',    minRank: 17,    maxRank: 32,    percent: 10 }, // ~0.625 each
      { stage: 'Round 6',    minRank: 33,    maxRank: 64,    percent: 10 }, // ~0.15625 each
      { stage: 'Round 5',   minRank: 65,    maxRank: 128,   percent: 9 },
      { stage: 'Round 4',   minRank: 129,   maxRank: 256,   percent: 7 },
      { stage: 'Round 3',   minRank: 257,   maxRank: 512,   percent: 6 },
      { stage: 'Round 2', minRank: 513,   maxRank: 1024,  percent: 4 },
    ],
  },

  // 8,192 teams — pays Top 2,048
  'bracket-8k-team': {
    name: 'Bracket - 8,192 Slots (Pays Top 2,048)',
    plainName: 'Bracket - 8,192 Slots',
    description: 'Round-based payouts through top 2,048 slots.',
    plainDescription: 'Single-elimination bracket: 8,192 slots, 13 rounds.',
    totalRounds: 13, 
    bands: [
      { stage: 'Champion',       minRank: 1,     maxRank: 1,     percent: 15 },
      { stage: 'Runner-up',      minRank: 2,     maxRank: 2,     percent: 9 },
      { stage: 'Semifinals',     minRank: 3,     maxRank: 4,     percent: 9 },  // 4.5 each
      { stage: 'Quarterfinals',  minRank: 5,     maxRank: 8,     percent: 9 },  // 2.25 each
      { stage: 'Round 9',    minRank: 9,     maxRank: 16,    percent: 9 },  // 1.125 each
      { stage: 'Round 8',    minRank: 17,    maxRank: 32,    percent: 9 },  // ~0.5625 each
      { stage: 'Round 7',    minRank: 33,    maxRank: 64,    percent: 8 },
      { stage: 'Round 6',   minRank: 65,    maxRank: 128,   percent: 8 },
      { stage: 'Round 5',   minRank: 129,   maxRank: 256,   percent: 7 },
      { stage: 'Round 4',   minRank: 257,   maxRank: 512,   percent: 7 },
      { stage: 'Round 3', minRank: 513,   maxRank: 1024,  percent: 5 },
      { stage: 'Round 2', minRank: 1025,  maxRank: 2048,  percent: 5 },
    ],
  },

  // 16,384 teams — pays Top 4,096
  'bracket-16k-team': {
    name: 'Bracket - 16,384 Slots (Pays Top 4,096)',
    plainName: 'Bracket - 16,384 Slots',
    description: 'Round-based payouts through top 4,096 slots',
    plainDescription: 'Single-elimination bracket: 16,384 slots, 14 rounds.',
    totalRounds: 14,
    bands: [
      { stage: 'Champion',       minRank: 1,     maxRank: 1,     percent: 14 },
      { stage: 'Runner-up',      minRank: 2,     maxRank: 2,     percent: 8 },
      { stage: 'Semifinals',     minRank: 3,     maxRank: 4,     percent: 10 }, // 5 each
      { stage: 'Quarterfinals',  minRank: 5,     maxRank: 8,     percent: 9 },
      { stage: 'Round 10',    minRank: 9,     maxRank: 16,    percent: 9 },
      { stage: 'Round 9',    minRank: 17,    maxRank: 32,    percent: 9 },
      { stage: 'Round 8',    minRank: 33,    maxRank: 64,    percent: 8 },
      { stage: 'Round 7',   minRank: 65,    maxRank: 128,   percent: 8 },
      { stage: 'Round 6',   minRank: 129,   maxRank: 256,   percent: 7 },
      { stage: 'Round 5',   minRank: 257,   maxRank: 512,   percent: 6 },
      { stage: 'Round 4', minRank: 513,   maxRank: 1024,  percent: 5 },
      { stage: 'Round 3', minRank: 1025,  maxRank: 2048,  percent: 4 },
      { stage: 'Round 2', minRank: 2049,  maxRank: 4096,  percent: 3 },
    ],
  },

    // 32,768 teams — pays Top 8,192
  'bracket-32k-team': {
    name: 'Bracket - 32,768 Slots (Pays Top 8,192)',
    plainName: 'Bracket - 32,768 Slots',
    description: 'Round-based payouts through top 8,192 slots.',
    plainDescription: 'Single-elimination bracket: 32,768 slots, 15 rounds.',
    totalRounds: 15,
    bands: [
      { stage: 'Champion',       minRank: 1,     maxRank: 1,     percent: 13 },
      { stage: 'Runner-up',      minRank: 2,     maxRank: 2,     percent: 8 },
      { stage: 'Semifinals',     minRank: 3,     maxRank: 4,     percent: 9 },  // 4.5 each
      { stage: 'Quarterfinals',  minRank: 5,     maxRank: 8,     percent: 9 },  // 2.25 each
      { stage: 'Round 11',       minRank: 9,     maxRank: 16,    percent: 9 },
      { stage: 'Round 10',       minRank: 17,    maxRank: 32,    percent: 8 },
      { stage: 'Round 9',        minRank: 33,    maxRank: 64,    percent: 8 },
      { stage: 'Round 8',        minRank: 65,    maxRank: 128,   percent: 7 },
      { stage: 'Round 7',        minRank: 129,   maxRank: 256,   percent: 7 },
      { stage: 'Round 6',        minRank: 257,   maxRank: 512,   percent: 6 },
      { stage: 'Round 5',        minRank: 513,   maxRank: 1024,  percent: 5 },
      { stage: 'Round 4',        minRank: 1025,  maxRank: 2048,  percent: 5 },
      { stage: 'Round 3',        minRank: 2049,  maxRank: 4096,  percent: 4 },
      { stage: 'Round 2',        minRank: 4097,  maxRank: 8192,  percent: 2 },
    ],
  },

  // ------------------------
  // Who’s Got Next? (streak-based)
  // Split each band evenly among all players who hit that streak.
  // ------------------------
  'wgn-6': {
    name: "Who's Got Next? — Streak 6",
    description: 'Pays to highest streaks achieved: 6, then 5, then 4. Streak resets if you buy back in.',
    plainDescription: 'Winner stays on. Top streaks: 6, then 5, then 4. Your streak resets if you re-enter.',
    bands: [
      { stage: 'Streak 6', minRank: 1, maxRank: 1, percent: 60 },
      { stage: 'Streak 5', minRank: 1, maxRank: 1, percent: 25 },
      { stage: 'Streak 4', minRank: 1, maxRank: 1, percent: 15 },
    ],
  },
  'wgn-5': {
    name: "Who's Got Next? — Streak 5",
    description: 'Pays to highest streak achieved: 5, then 4. Streak resets if you buy back in.',
    plainDescription: 'Winner stays on. Top streaks: 5, then 4. Your streak resets if you re-enter.',
    bands: [
      { stage: 'Streak 5', minRank: 1, maxRank: 1, percent: 70 },
      { stage: 'Streak 4', minRank: 1, maxRank: 1, percent: 30 },
    ],
  },
  'wgn-4': {
    name: "Who's Got Next? — Streak 4",
    description: 'Pays to streak 4, then 3. Streak resets if you buy back in.',
    plainDescription: 'Winner stays on. Top streaks: 4, then 3. Your streak resets if you re-enter.',
    bands: [
      { stage: 'Streak 4', minRank: 1, maxRank: 1, percent: 65 },
      { stage: 'Streak 3', minRank: 1, maxRank: 1, percent: 35 },
    ],
  },

  // ------------------------
  // Season Ladder — Top 100 placements
  // `percent` is total for the band; split evenly across that rank range.
  // ------------------------
  'season-ladder-100': {
    name: 'Season Ladder — Pays Top 100',
    plainName: 'Season Ladder — Top 100',
    description: 'Leaderboard-style payouts to the top 100 season placements.',
    plainDescription: 'A season-long leaderboard of the top 100 placements.',
    bands: [
      { stage: 'King Crit', minRank: 1,   maxRank: 1,   percent: 18 },
      { stage: 'Crown Regent', minRank: 2,   maxRank: 2,   percent: 9 },
      { stage: 'Grand Duke', minRank: 3,   maxRank: 3,   percent: 6 },
      { stage: 'Marquess', minRank: 4,   maxRank: 4,   percent: 4.5 },
      { stage: 'Count', minRank: 5,   maxRank: 5,   percent: 3.5 },
      { stage: 'Viscounts', minRank: 6,   maxRank: 10,  percent: 10 }, // 2 each
      { stage: 'Barons', minRank: 11,  maxRank: 20,  percent: 10 }, // 1 each
      { stage: 'Knights', minRank: 21,  maxRank: 40,  percent: 16 }, // 0.8 each
      { stage: 'Squires', minRank: 41,  maxRank: 60,  percent: 10 }, // 0.5 each
      { stage: 'Heralds', minRank: 61,  maxRank: 80,  percent: 7 },  // 0.35 each
      { stage: 'Citizens of Crit', minRank: 81,  maxRank: 100, percent: 5 },  // 0.25 each
    ],
  },

  // ---- ----------------------- ---- //
  // --------- GT7 --- Racing Leauge!!
  // --------------------------------- //

  'racing-GT7-16k': {
    name: 'Grand Touring Championship',
    description: 'The pinnacle of racing. Face thousands in the ultimate test of skill, strategy, and endurance. Only one can forge a legacy and claim the lion\'s share of a massive Crit pool. Your championship run starts here.',
    plainDescription: 'The pinnacle of racing: the ultimate test of skill, strategy, and endurance. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 15,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 45,
        subBands: [
          { tier: 'Champion',         places: [1], percentOfBand: 30 },
          { tier: 'Second Place',     places: [2], percentOfBand: 18 },
          { tier: 'Third Place',      places: [3], percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 128',   
        percent: 2.5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 256',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 512',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 1,024',   
        percent: 1.5, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2,048',  
        percent: 1.5,   // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4,096',  
        percent: 1.5,   // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  
  'racing-GT7-8k': {
    name: 'Apex Majors',
    description: 'A high-pressure major where only the elite thrive. Battle through a massive field where every corner matters. This isn\'t just a race; it\'s a statement. Secure your spot among the best and drive away with a huge payday.',
    plainDescription: 'A high-pressure major where only the elite thrive. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 14,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 46.5,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 128',   
        percent: 2.5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 256',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 512',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 1,024',   
        percent: 1.5, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2,048',  
        percent: 1.5,   // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  'racing-GT7-4k': {
    name: 'Sovereign Classic',
    description: 'A prestigious event for the purist. Navigate a field of thousands in this multi-stage classic. Glory and substantial prizes await those who master the circuit and prove their class.',
    plainDescription: 'A multi-stage classic for the purist. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 13,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 48,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 128',   
        percent: 2.5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 256',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 512',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 1,024',   
        percent: 1.5, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
    'racing-GT7-2k': {
    name: 'Gauntlet Invitational',
    description: 'Do you have what it takes to survive the Gauntlet? This grueling invitational will test your limits against a field of formidable drivers. Big risks, bigger rewards. Are you in?',
    plainDescription: 'A grueling invitational against a field of formidable drivers. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 12,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 49.5,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 128',   
        percent: 2.5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 256',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 512',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  'racing-GT7-1k': {
    name: 'Checkered Flag Championship',
    description: 'The checkered flag is in sight, but a thousand racers stand in your way. Fight through the pack, claim the championship title, and take home the glory—and the cash.',
    plainDescription: 'Fight through the pack and claim the championship title. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 11,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 51.5,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 128',   
        percent: 2.5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 256',   
        percent: 2, // Corrected and filled in
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
    'racing-GT7-512': {
    name: 'Thunder Cup',
    description: 'Unleash the thunder. A high-octane battle where consistency and aggression pay off. Weave through a sizable field to claim victory in the Thunder Cup and a roar of your own.',
    plainDescription: 'A high-octane battle where consistency and aggression count. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 10,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 53.5,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 128',   
        percent: 2.5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
    'racing-GT7-256': {
    name: 'Slipstream Challenge',
    description: 'This isn\'t just about speed—it\'s about strategy. Outsmart and outmaneuver the competition by mastering the draft. A few clever moves are all that separate you from the front of the pack and a significant prize.',
    plainDescription: 'Speed and strategy: master the draft to reach the front of the pack. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 9,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 56,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 64',     
        percent: 3,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
    'racing-GT7-128': {
    name: 'Apex Pursuit',
    description: 'The pursuit is on. A condensed field means every race is a close-quarters battle for position. Hunt down the leaders, defend your line, and prove you\'re the predator, not the prey.',
    gridSize: 15,
    totalRounds: 8,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 59,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 32',     
        percent: 4,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
    'racing-GT7-64': {
    name: 'Grid Gauntlet',
    description: 'The lights go out on the ultimate showdown. With a packed grid and fewer heats, there\'s no holding back. A few perfect races are all that stand between you and a quick, decisive victory.',
    gridSize: 15,
    totalRounds: 7,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 63,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 16',     
        percent: 5,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  'racing-GT7-32': {
    name: 'Velocity Open',
    description: 'Jump into the fast lane. This open-format tournament is all about pure speed and bold overtakes. Outdrive the competition in a handful of heats to claim the top prize. Easy to enter, thrilling to win.',
    plainDescription: 'Pure speed and bold overtakes. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 6,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 68,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 8',     
        percent: 7,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  'racing-GT7-16': {
    name: 'Paddock Sprint',
    description: 'From the paddock to the podium. This is an all-out sprint for the top prize. With only one elimination heat, you\'re just one great race away from the winner-decides-it-all final. No long-term strategy, just pure speed.',
    plainDescription: 'From the paddock to the podium: an all-out sprint. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 5,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 75,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 15,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 4',   
        percent: 10,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  'racing-GT7-8': {
    name: 'Chicane Clash',
    description: 'Master the corners, master the competition. This technical clash is a true test of driver nerve, where one mistake can cost you everything. Navigate the chaos of the first heat to earn your shot at the final prize.',
    plainDescription: 'A technical test of driver nerve. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 4,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 86,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
      { 
        stage: 'Heat of 2',      
        percent: 14,
        subBands: [
          { tier: 'Near Miss',   places: [9, 10], percentOfBand: 50 },
          { tier: 'Mid-Pack',    places: [11, 13], percentOfBand: 30 },
          { tier: 'Bottom-Pack', places: [14, 15], percentOfBand: 20 },
        ]
      },
    ],
  },
  'racing-GT7-4': {
    name: 'Lightning Club Cup',
    description: 'An exclusive battle for the podium. Four racers start, but only the top three get paid. With the entire Crit pool up for grabs in a single race, every split-second decision matters. Are you fast enough?',
    plainDescription: 'An exclusive battle for the podium. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 15,
    totalRounds: 3,
    bands: [
      { 
        stage: 'Finals',          
        minRank: 1,      
        maxRank: 15,
        percent: 100,
        subBands: [
          { tier: 'Champion',         places: [1],    percentOfBand: 30 },
          { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
          { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
          { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
          { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
        ]
      },
    ],
  },

  'racing-GT7-2': {
    name: 'Double Header Final',
    description: 'Two semis feed a 15-car final. Finals-only payout.',
    plainDescription: 'Two semis feed a 15-car final.',
    gridSize: 15,
    totalRounds: 2,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 15, percent: 100, subBands: [
        { tier: 'Champion',         places: [1],    percentOfBand: 30 },
        { tier: 'Second Place',     places: [2],    percentOfBand: 18 },
        { tier: 'Third Place',      places: [3],    percentOfBand: 12 },
        { tier: 'Top 8 Finalist',   places: [4, 8], percentOfBand: 20 },
        { tier: 'Finals Finisher',  places: [9, 15],percentOfBand: 20 },
      ]},
    ],
  },

  'racing-GT7-top-7':{
    name: 'Showdown',
    description: 'The challenge is simple: finish in the top half to get paid. This is a one-race showdown where every position in the top 7 earns a piece of the prize. Fight for the podium or secure your spot in the money—every pass counts.',
    plainDescription: 'One-race showdown (15-car grid). Fight for the podium; every pass counts.',
    gridSize: 15,
    totalRounds: 1,
    bands: [
      {tier: 'Champion', minRank: 1, maxRank: 1, percent: 40 },
      {tier: '2nd Place', minRank: 2, maxRank: 2, percent: 25},
      {tier: '3rd Place', minRank: 3, maxRank: 3, percent: 15},
      {tier: 'In The Money', minRank: 4, maxRank: 7, percent: 20},
    ]
  },

  // ==========================================================================
  // WARZONE (cod battle royale) - single-lobby placement payouts.
  // MIRROR of functions/constants/payoutStructures.js wz-* entries - keep the
  // bands in sync if the backend tables are retuned.
  // ==========================================================================

  // Solos: finalRank on each entry is the player's own placement.
  'wz-solos-top-10': {
    name: 'Warzone Solos - Top 10',
    description: 'Drop in and outlast the lobby. The top 10 placements each earn a piece of the prize - the deeper you survive, the bigger your cut.',
    plainDescription: 'Drop in and outlast the lobby. Final placement decides the standings: the deeper you survive, the higher you finish.',
    gridSize: 50,
    totalRounds: 1,
    bands: [
      { tier: 'Champion',   minRank: 1,  maxRank: 1,  percent: 30 },
      { tier: '2nd Place',  minRank: 2,  maxRank: 2,  percent: 20 },
      { tier: '3rd Place',  minRank: 3,  maxRank: 3,  percent: 14 },
      { tier: '4th Place',  minRank: 4,  maxRank: 4,  percent: 10 },
      { tier: '5th Place',  minRank: 5,  maxRank: 5,  percent: 8  },
      { tier: '6th Place',  minRank: 6,  maxRank: 6,  percent: 6  },
      { tier: '7th Place',  minRank: 7,  maxRank: 7,  percent: 4  },
      { tier: '8th Place',  minRank: 8,  maxRank: 8,  percent: 3  },
      { tier: '9th Place',  minRank: 9,  maxRank: 9,  percent: 3  },
      { tier: '10th Place', minRank: 10, maxRank: 10, percent: 2  },
    ],
  },

  // Squads (Duos/Trios/Quads): finalRank on each entry is the SQUAD's
  // placement; each band's amount is the squad's share, split equally
  // among the entries holding that finalRank (perSquad).
  'wz-squads-top-5': {
    name: 'Warzone Squads - Top 5',
    description: 'Squad up and fight to the final circle. The top 5 squads share the prize - every member of a placing squad gets an equal cut of their squad\'s payout.',
    plainDescription: 'Squad up and fight to the final circle. Squads are ranked by final placement, and every member shares their squad\'s placement.',
    gridSize: 50,
    totalRounds: 1,
    perSquad: true,
    bands: [
      { tier: 'Champion Squad', minRank: 1, maxRank: 1, percent: 40 },
      { tier: '2nd Squad',      minRank: 2, maxRank: 2, percent: 25 },
      { tier: '3rd Squad',      minRank: 3, maxRank: 3, percent: 15 },
      { tier: '4th Squad',      minRank: 4, maxRank: 4, percent: 12 },
      { tier: '5th Squad',      minRank: 5, maxRank: 5, percent: 8  },
    ],
  },

// ============================================================================
// iRACING (iRC) PAYOUT STRUCTURES - 24-car grid
// ============================================================================
// Add these entries to PAYOUT_STRUCTURES in payoutStructures.js
// Insert after the Forza (fzh) entries or replace them

  // ========================
  // iRacing (iRC) — 24-car grid
  // ========================

  'racing-irc-16k': {
    name: 'iRacing World Championship',
    description: 'The pinnacle of sim racing. Thousands of the world\'s best drivers compete through grueling heats for a shot at the ultimate prize. Precision, consistency, and racecraft will be tested at every turn.',
    plainDescription: 'The pinnacle of sim racing. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 24,
    totalRounds: 15,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 45, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 1,024', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2,048', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4,096', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-8k': {
    name: 'iRacing Pro Series',
    description: 'Elite competition where every tenth of a second matters. Battle through a massive field of sim racers vying for their share of a substantial Crit pool.',
    plainDescription: 'Elite competition where every tenth of a second matters. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 24,
    totalRounds: 14,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 46.5, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-4k': {
    name: 'iRacing Continental Series',
    description: 'Regional champions converge in an intense battle through iconic circuits.',
    gridSize: 24,
    totalRounds: 13,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 48, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-2k': {
    name: 'iRacing National Cup',
    description: 'Prove yourself against the nation\'s best. Clean racing and smart strategy rule the day.',
    gridSize: 24,
    totalRounds: 12,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 50, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-1k': {
    name: 'iRacing Premier League',
    description: 'Where serious sim racers prove their worth. Compete through multiple heats to reach the grand final.',
    gridSize: 24,
    totalRounds: 11,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 55, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4.5, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-512': {
    name: 'iRacing Elite Sprint',
    description: 'A focused sprint through competitive heats. Every position counts.',
    gridSize: 24,
    totalRounds: 10,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 60, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 8,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 7,  subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-256': {
    name: 'iRacing Challenge Cup',
    description: 'A demanding gauntlet for aspiring champions.',
    gridSize: 24,
    totalRounds: 9,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 65, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-128': {
    name: 'iRacing Regional Series',
    description: 'Compete against local talent for regional bragging rights.',
    gridSize: 24,
    totalRounds: 8,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 70, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-64': {
    name: 'iRacing Club Championship',
    description: 'Club-level competition with serious prizes.',
    plainDescription: 'Club-level competition. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 24,
    totalRounds: 7,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 78, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 22, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-32': {
    name: 'iRacing Sprint Cup',
    description: 'Quick heats, fast cars, and instant pressure.',
    gridSize: 24,
    totalRounds: 6,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 82, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 18, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-16': {
    name: 'iRacing Mini Enduro',
    description: 'A compact endurance event testing skill and consistency.',
    gridSize: 24,
    totalRounds: 5,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 84, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 16, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-8': {
    name: 'iRacing Dash',
    description: 'Two quick heats feed a final showdown. Every lap matters.',
    gridSize: 24,
    totalRounds: 4,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 86, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 14, subBands: [
        { tier: 'Near Miss',   places: [13, 16], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [17, 20], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [21, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-4': {
    name: 'iRacing Quick Race',
    description: 'Four grids, one final, winner takes the glory.',
    gridSize: 24,
    totalRounds: 3,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 100, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-2': {
    name: 'iRacing Double Header',
    description: 'Two heats feed a 24-car final. Finals-only payout.',
    plainDescription: 'Two heats feed a 24-car final.',
    gridSize: 24,
    totalRounds: 2,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 24, percent: 100, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 12 Finalist', places: [4, 12],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [13, 24], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-irc-top-12': {
    name: 'Showdown',
    description: 'Finish in the top half to get paid. One-race showdown (24-car grid).',
    plainDescription: 'One-race showdown (24-car grid). Every position counts.',
    gridSize: 24,
    totalRounds: 1,
    bands: [
      { tier: 'Champion',     minRank: 1,  maxRank: 1,  percent: 40 },
      { tier: '2nd Place',    minRank: 2,  maxRank: 2,  percent: 25 },
      { tier: '3rd Place',    minRank: 3,  maxRank: 3,  percent: 15 },
      { tier: 'In The Money', minRank: 4,  maxRank: 12, percent: 20 },
    ],
  },


// ============================================================================
// F1 25 (F25) PAYOUT STRUCTURES - 20-car grid
// ============================================================================

  // ========================
  // F1 25 (F25) — 20-car grid (F1 standard)
  // ========================

  'racing-f25-16k': {
    name: 'F1 World Championship',
    description: 'The ultimate virtual Grand Prix championship. Race wheel-to-wheel through iconic circuits as thousands compete for glory and a massive Crit pool. Only the fastest will stand on the podium.',
    plainDescription: 'The ultimate virtual Grand Prix championship. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 20,
    totalRounds: 15,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 45, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 1,024', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2,048', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4,096', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-8k': {
    name: 'F1 Grand Prix Masters',
    description: 'Elite competition across the world\'s most demanding circuits. Tire strategy and clean racing will determine the champion.',
    gridSize: 20,
    totalRounds: 14,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 46.5, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-4k': {
    name: 'F1 Continental Cup',
    description: 'Regional champions clash in a high-speed battle for supremacy.',
    gridSize: 20,
    totalRounds: 13,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 48, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-2k': {
    name: 'F1 National Series',
    description: 'Prove yourself against the best in your region.',
    gridSize: 20,
    totalRounds: 12,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 50, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-1k': {
    name: 'F1 Pro League',
    description: 'Where serious F1 gamers compete for bragging rights and prizes.',
    plainDescription: 'Where serious F1 gamers compete for bragging rights. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 20,
    totalRounds: 11,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 55, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4.5, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-512': {
    name: 'F1 Sprint Series',
    description: 'Fast heats, faster cars, quickest driver wins.',
    gridSize: 20,
    totalRounds: 10,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 60, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 8,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 7,  subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-256': {
    name: 'F1 Challenge Cup',
    description: 'A demanding test for aspiring Grand Prix champions.',
    gridSize: 20,
    totalRounds: 9,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 65, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-128': {
    name: 'F1 Regional Trophy',
    description: 'Compete against local talent for regional glory.',
    gridSize: 20,
    totalRounds: 8,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 70, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-64': {
    name: 'F1 Club Championship',
    description: 'Club-level competition with real prizes.',
    plainDescription: 'Club-level competition. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 20,
    totalRounds: 7,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 78, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 22, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-32': {
    name: 'F1 Quick Cup',
    description: 'Fast format, fast cars, fast payouts.',
    plainDescription: 'Fast format, fast cars. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 20,
    totalRounds: 6,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 82, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 18, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-16': {
    name: 'F1 Mini GP',
    description: 'A compact Grand Prix format with real rewards.',
    plainDescription: 'A compact Grand Prix format. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 20,
    totalRounds: 5,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 84, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 16, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-8': {
    name: 'F1 Dash',
    description: 'Two heats, one final, winner takes all.',
    plainDescription: 'A short, sharp dash. The top finishers in each heat advance, round by round, to the final.',
    gridSize: 20,
    totalRounds: 4,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 86, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 14, subBands: [
        { tier: 'Near Miss',   places: [11, 14], percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [15, 17], percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [18, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-4': {
    name: 'F1 Sprint Final',
    description: 'Four grids, one final showdown.',
    gridSize: 20,
    totalRounds: 3,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 100, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-2': {
    name: 'F1 Double Header',
    description: 'Two heats feed a 20-car final. Finals-only payout.',
    plainDescription: 'Two heats feed a 20-car final.',
    gridSize: 20,
    totalRounds: 2,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 20, percent: 100, subBands: [
        { tier: 'Champion',        places: [1],      percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],      percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],      percentOfBand: 12 },
        { tier: 'Top 10 Finalist', places: [4, 10],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [11, 20], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-f25-top-10': {
    name: 'Showdown',
    description: 'Finish in the top half to get paid. One-race showdown (20-car grid).',
    plainDescription: 'One-race showdown (20-car grid). Every position counts.',
    gridSize: 20,
    totalRounds: 1,
    bands: [
      { tier: 'Champion',     minRank: 1,  maxRank: 1,  percent: 40 },
      { tier: '2nd Place',    minRank: 2,  maxRank: 2,  percent: 25 },
      { tier: '3rd Place',    minRank: 3,  maxRank: 3,  percent: 15 },
      { tier: 'In The Money', minRank: 4,  maxRank: 10, percent: 20 },
    ],
  },
  // ========================
  // Mario Kart World (MKW) — 12-kart grid
  // ========================

  'racing-mkw-16k': {
    name: 'Kart Grand Prix World Championship',
    description: 'The ultimate karting gauntlet across cups and continents.',
    gridSize: 12,
    totalRounds: 15,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 45, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 1,024', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2,048', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4,096', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-8k': {
    name: 'Star Circuit Majors',
    description: 'Elite fields, item chaos, and perfect lines.',
    gridSize: 12,
    totalRounds: 14,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 46.5, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-4k': {
    name: 'Mushroom Masters',
    description: 'Prestige grid—tight packs, smart item usage.',
    gridSize: 12,
    totalRounds: 13,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 48, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',     percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',     percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',     percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',    percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',    percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',    percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128',   percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512',   percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 1,024', percent: 1.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-2k': {
    name: 'Lightning Invitational',
    description: 'Invite-only brackets. Clean racing under pressure.',
    gridSize: 12,
    totalRounds: 12,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 49.5, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',  percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',  percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',  percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128', percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 256', percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 512', percent: 2,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-1k': {
    name: 'Rainbow Cup Championship',
    description: 'A thousand racers. Hold your drift and your nerve.',
    gridSize: 12,
    totalRounds: 11,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 51.5, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',  percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',  percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-512': {
    name: 'Turbo Tour',
    description: 'Aggressive packs and tight slipstreams—timing is everything.',
    gridSize: 12,
    totalRounds: 10,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 53.5, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',  percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',  percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',  percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 128', percent: 2.5, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-256': {
    name: 'Drift Rally',
    description: 'Lines, hops, and mini-turbos—consistency pays.',
    plainDescription: 'Lines, hops, and mini-turbos. Consistency wins.',
    gridSize: 12,
    totalRounds: 9,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 56, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',  percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',  percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 64',  percent: 3,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-128': {
    name: 'Shell Series',
    description: 'Close quarters and shell management decide everything.',
    gridSize: 12,
    totalRounds: 8,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 59, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',  percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 32',  percent: 4,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-64': {
    name: 'Banana Cup Showdown',
    description: 'Few races, huge pressure—avoid the peels and the pack.',
    gridSize: 12,
    totalRounds: 7,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 63, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 16',  percent: 5,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-32': {
    name: 'Flower Cup Open',
    description: 'Pure pace, perfect items, bold passes.',
    gridSize: 12,
    totalRounds: 6,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 68, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 8',   percent: 7,   subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-16': {
    name: 'Karting Sprint',
    description: 'One elimination heat, then the decisive final.',
    gridSize: 12,
    totalRounds: 5,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 75, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2',   percent: 15,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 4',   percent: 10,  subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-8': {
    name: 'Choco Mountain Clash',
    description: 'Technical layouts and tight packs—execute or get item’d.',
    gridSize: 12,
    totalRounds: 4,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 86, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
      { stage: 'Heat of 2', percent: 14, subBands: [
        { tier: 'Near Miss',   places: [7, 8],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    places: [9, 10],  percentOfBand: 30 },
        { tier: 'Bottom-Pack', places: [11, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-4': {
    name: 'Toad’s Club Cup',
    description: 'Single-race final—top three take the big slices.',
    plainDescription: 'Single-race final. Race for the podium.',
    gridSize: 12,
    totalRounds: 3,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 100, subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',  places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [7, 12], percentOfBand: 20 },
      ]},
    ],
  },

  'racing-mkw-2': {
    name: 'Double Dash Final',
    description: 'Two semis feed a 12-kart final. Finals-only payout.',
    plainDescription: 'Two semis feed a 12-kart final.',
    gridSize: 12,
    totalRounds: 2,
    bands: [
      { stage: 'Finals', minRank: 1, maxRank: 12, percent: 100, subBands: [
        { tier: 'Champion',         places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',     places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',      places: [3],     percentOfBand: 12 },
        { tier: 'Top 6 Finalist',   places: [4, 6],  percentOfBand: 20 },
        { tier: 'Finals Finisher',  places: [7, 12], percentOfBand: 20 },
      ]},
    ],
  },

  // One-race payout for a 12-kart grid: pay the top half
  'racing-mkw-top-6': {
    name: 'Showdown',
    description: 'Finish in the top half to get paid. One-race showdown (12-kart grid).',
    plainDescription: 'One-race showdown (12-kart grid). Every position counts.',
    gridSize: 12,
    totalRounds: 1,
    bands: [
      { tier: 'Champion',   minRank: 1, maxRank: 1, percent: 40 },
      { tier: '2nd Place',  minRank: 2, maxRank: 2, percent: 25 },
      { tier: '3rd Place',  minRank: 3, maxRank: 3, percent: 15 },
      { tier: 'In The Money', minRank: 4, maxRank: 6, percent: 20 },
    ],
  },
};

// ============================================================================
// GT7 HEATS BANDS ARE GENERATED — one family, settlement-derived ranges.
// MIRROR: this block exists identically in functions/constants/ and
// web-landing/src/constants/ payoutStructures.js — keep them in sync.
//
// The 'racing-GT7-N' band literals above are SUPERSEDED at load by
// buildRacingHeatsBands(totalRounds): EVERY elimination round gets a stage
// band (canonical taper 15/10/7/5/4/3/2.5/2/2/1.5/1.5/…, nearest-to-finals
// first; Finals takes the remainder — the old tables stopped two rounds
// short, paying ~22% of a large field). Elimination sub-bands carry offsets
// FROM THE CUT (placesFromCut) so settlement pays the ACTUAL eliminated
// range of any grid shape: 15-deal cut 7 → places 8-15, hosted-racer
// 16-deal cut 8 → 9-16, underfilled heats their real places — closing the
// old place-8/place-16 holes with ONE table. The legacy `places` arrays
// stay for preview rendering (15-grid shape). Finals covers ranks 1-16
// (hosted-racer finals seat 16 racers; the pull-in fills seat 15 on
// spectating grids, so rank 15 is reachable — unreached ranks burn).
// ============================================================================
const RACING_HEATS_STAGE_PERCENTS = [15, 10, 7, 5, 4, 3, 2.5, 2, 2, 1.5, 1.5, 1.5, 1.5, 1.5];

function buildRacingHeatsBands(totalRounds) {
  const R = Math.max(2, Math.floor(Number(totalRounds) || 2));
  const stages = [];
  let eliminationTotal = 0;
  for (let round = R - 1; round >= 1; round--) {
    const back = R - round; // 1 = 'Heat of 2' (the round before finals)
    const pct = RACING_HEATS_STAGE_PERCENTS[back - 1] != null
      ? RACING_HEATS_STAGE_PERCENTS[back - 1] : 1.5;
    eliminationTotal += pct;
    stages.push({
      stage: `Heat of ${Math.pow(2, back).toLocaleString('en-US')}`,
      percent: pct,
      subBands: [
        { tier: 'Near Miss',   placesFromCut: [1, 2], places: [8, 9],   percentOfBand: 50 },
        { tier: 'Mid-Pack',    placesFromCut: [3, 5], places: [10, 12], percentOfBand: 30 },
        { tier: 'Bottom-Pack', placesFromCut: [6, 8], places: [13, 15], percentOfBand: 20 },
      ],
    });
  }
  return [
    {
      stage: 'Finals',
      minRank: 1,
      maxRank: 16,
      percent: Math.round((100 - eliminationTotal) * 10) / 10,
      subBands: [
        { tier: 'Champion',        places: [1],     percentOfBand: 30 },
        { tier: 'Second Place',    places: [2],     percentOfBand: 18 },
        { tier: 'Third Place',     places: [3],     percentOfBand: 12 },
        { tier: 'Top 8 Finalist',  places: [4, 8],  percentOfBand: 20 },
        { tier: 'Finals Finisher', places: [9, 16], percentOfBand: 20 },
      ],
    },
    ...stages,
  ];
}

for (const [gt7Id, gt7Struct] of Object.entries(PAYOUT_STRUCTURES)) {
  if (/^racing-GT7-\d+k?$/.test(gt7Id)) {
    gt7Struct.bands = buildRacingHeatsBands(gt7Struct.totalRounds);
  }
}

// ============================================================================
// Member-facing structure text. `description` is the Crit-era copy (pools,
// payouts, prizes); `plainDescription` is the format-only, money-free twin on
// every structure whose description talks money. Crits ON renders the
// description byte-identical; Crits OFF renders the plain text (falling back
// to the description, which is then already money-free). Every screen that
// shows a structure's text goes through this, never `.description` directly.
// structureName is the same rule for `name` / `plainName` ("Pays Top 4",
// "Winner Take All" names).
// ============================================================================
export function structureText(struct, critsOn) {
  if (!struct) return '';
  return critsOn ? struct.description : (struct.plainDescription || struct.description);
}

export function structureName(struct, critsOn) {
  if (!struct) return '';
  return critsOn ? struct.name : (struct.plainName || struct.name);
}
